/**
 * Frameworks tab — browse the 144 coaching frameworks with their
 * credibility statements + external citations + linked academic sources.
 *
 * Frameworks live separate from research_sources (they're coaching
 * methodologies, not papers). Credibility data was populated by the
 * 2026-04-28 backfill (migration 1231 + backfill script).
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Award, BookOpen, ExternalLink, Eye, X } from 'lucide-react';
import { adminFetch } from '../../../utils/adminApi';
import type { ExternalCitation, FrameworkListItem, PaginatedResponse } from './types';

const PAGE_SIZE = 30;

interface FrameworkDetail extends FrameworkListItem {
  source_work?: string;
  research_interpretation?: string;
  consensus_deviation?: string;
  key_principles?: string[];
  practical_guidelines?: string[];
  goal_contexts?: string[];
  condition_contexts?: string[];
  demographic_contexts?: string[];
  sport_contexts?: string[];
  linked_sources: Array<{
    id: string;
    title: string;
    authors: string[] | null;
    journal: string | null;
    publication_year: number | null;
    quality_score: number | null;
    evidence_strength: string | null;
    methodology_type: string | null;
  }>;
}

function consensusColor(score: number | null) {
  if (score == null) return 'text-gray-500';
  if (score >= 0.85) return 'text-emerald-700';
  if (score >= 0.70) return 'text-blue-700';
  if (score >= 0.50) return 'text-amber-700';
  return 'text-rose-700';
}

function CitationItem({ c }: { c: ExternalCitation }) {
  const typeBadge = {
    book: 'bg-amber-50 text-amber-800',
    podcast: 'bg-purple-50 text-purple-800',
    conference: 'bg-blue-50 text-blue-800',
    website: 'bg-cyan-50 text-cyan-800',
    journal_paper: 'bg-emerald-50 text-emerald-800',
  }[c.type] || 'bg-gray-50 text-gray-700';

  return (
    <div className="rounded border border-gray-100 bg-white p-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${typeBadge}`}>
          {c.type}
        </span>
        {c.year && <span className="text-gray-500">{c.year}</span>}
      </div>
      <div className="mt-1 font-medium text-gray-900">{c.title}</div>
      {c.author && <div className="text-gray-600">{c.author}</div>}
      {c.url && (
        <a href={c.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-blue-600 hover:underline">
          source <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {c.raw && !c.title && <div className="text-gray-500">{c.raw}</div>}
    </div>
  );
}

function FrameworkDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ['rk', 'framework-detail', id],
    queryFn: () => adminFetch<FrameworkDetail>(`/api/admin/researcher-knowledge/frameworks/${id}`),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 flex items-start justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900 pr-8">
            {detail.data?.framework_name || 'Loading…'}
          </h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        {detail.data && (
          <div className="space-y-5 p-6">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {detail.data.evidence_basis && (
                <span className="rounded bg-blue-50 px-2 py-0.5 font-medium text-blue-800">
                  {detail.data.evidence_basis}
                </span>
              )}
              {detail.data.consensus_alignment_score != null && (
                <span className={`rounded bg-gray-100 px-2 py-0.5 font-medium ${consensusColor(detail.data.consensus_alignment_score)}`}>
                  Consensus: {(detail.data.consensus_alignment_score * 100).toFixed(0)}%
                </span>
              )}
              <span className="rounded bg-emerald-50 px-2 py-0.5 text-emerald-700">
                {detail.data.academic_ref_count} academic refs
              </span>
              <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-700">
                {detail.data.external_cite_count} external cites
              </span>
            </div>

            {detail.data.credibility_statement && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase text-blue-800">
                  <Award className="h-3 w-3" /> Credibility Statement
                </div>
                <p className="text-sm text-blue-900">{detail.data.credibility_statement}</p>
              </div>
            )}

            {detail.data.description && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Description</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.data.description}</p>
              </div>
            )}

            {detail.data.source_work && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Source Work</h4>
                <p className="text-sm text-gray-700">{detail.data.source_work}</p>
              </div>
            )}

            {(detail.data.external_citations?.length || 0) > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">
                  External Citations ({detail.data.external_citations.length})
                </h4>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {detail.data.external_citations.map((c, i) => (
                    <CitationItem key={i} c={c} />
                  ))}
                </div>
              </div>
            )}

            {(detail.data.linked_sources?.length || 0) > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">
                  Linked Academic Sources ({detail.data.linked_sources.length})
                </h4>
                <ul className="space-y-2 text-sm">
                  {detail.data.linked_sources.map((s) => (
                    <li key={s.id} className="rounded border border-gray-100 bg-gray-50 p-2">
                      <div className="font-medium text-gray-900">{s.title}</div>
                      <div className="text-xs text-gray-600">
                        {(s.authors || []).join(', ')} · {s.journal} {s.publication_year && `· ${s.publication_year}`}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs">
                        {s.quality_score != null && (
                          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800">
                            Q: {s.quality_score}
                          </span>
                        )}
                        {s.evidence_strength && (
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-800">
                            {s.evidence_strength}
                          </span>
                        )}
                        {s.methodology_type && (
                          <span className="rounded bg-violet-100 px-1.5 py-0.5 text-violet-800">
                            {s.methodology_type}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.data.consensus_deviation && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <h4 className="mb-1 text-xs font-semibold uppercase text-amber-800">
                  Consensus Deviation
                </h4>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">
                  {detail.data.consensus_deviation}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function FrameworksTab() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [evidenceBasis, setEvidenceBasis] = useState('');
  const [consensusMin, setConsensusMin] = useState(0);
  const [sortBy, setSortBy] = useState<'consensus_desc' | 'usage_30d' | 'name'>('consensus_desc');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const params = new URLSearchParams({
    consensus_min: String(consensusMin),
    sort_by: sortBy,
    page: String(page),
    page_size: String(PAGE_SIZE),
  });
  if (q) params.set('q', q);
  if (evidenceBasis) params.set('evidence_basis', evidenceBasis);

  const { data, isLoading } = useQuery({
    queryKey: ['rk', 'frameworks', params.toString()],
    queryFn: () =>
      adminFetch<PaginatedResponse<FrameworkListItem>>(
        `/api/admin/researcher-knowledge/frameworks?${params}`
      ),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search framework name or description…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          className="flex-1 min-w-[260px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
        />
        <select value={evidenceBasis} onChange={(e) => { setEvidenceBasis(e.target.value); setPage(1); }}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
          <option value="">All evidence types</option>
          <option value="peer_reviewed_consensus">Peer-reviewed consensus</option>
          <option value="research_backed">Research-backed</option>
          <option value="research_lead">Research-lead</option>
          <option value="experience_based">Experience-based</option>
          <option value="mixed">Mixed</option>
          <option value="theoretical">Theoretical</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
          <option value="consensus_desc">Consensus (high→low)</option>
          <option value="usage_30d">Most used (30d)</option>
          <option value="name">Name A→Z</option>
        </select>
        <div className="text-sm text-gray-500">Min consensus:
          <input type="number" min={0} max={100} step={5}
                 value={Math.round(consensusMin * 100)}
                 onChange={(e) => setConsensusMin((+e.target.value || 0) / 100)}
                 className="ml-1 w-16 rounded border border-gray-300 px-2 py-1 text-sm" />%
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Framework</th>
              <th className="px-3 py-2 text-left">Evidence basis</th>
              <th className="px-3 py-2 text-left">Consensus</th>
              <th className="px-3 py-2 text-left">Citations</th>
              <th className="px-3 py-2 text-right">Used 30d</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={6} className="p-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {data?.items.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{row.framework_name}</div>
                  <div className="text-xs text-gray-500 line-clamp-1">
                    {row.credibility_statement || row.description || '—'}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-800">
                    {row.evidence_basis || '—'}
                  </span>
                </td>
                <td className={`px-3 py-2 text-sm font-medium ${consensusColor(row.consensus_alignment_score)}`}>
                  {row.consensus_alignment_score != null
                    ? `${(row.consensus_alignment_score * 100).toFixed(0)}%`
                    : '—'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  <div className="flex gap-2">
                    <span title="Academic refs" className="inline-flex items-center gap-1">
                      <BookOpen className="h-3 w-3" /> {row.academic_ref_count}
                    </span>
                    <span title="External citations" className="inline-flex items-center gap-1 text-amber-700">
                      <Award className="h-3 w-3" /> {row.external_cite_count}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-gray-700">{row.usage_30d}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setSelectedId(row.id)}
                          className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs hover:bg-gray-100">
                    <Eye className="h-3 w-3" /> View
                  </button>
                </td>
              </tr>
            ))}
            {data?.items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-gray-500">No frameworks match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            {(data.page - 1) * data.page_size + 1}–
            {Math.min(data.page * data.page_size, data.total)} of {data.total.toLocaleString()}
          </div>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">
              Previous
            </button>
            <span className="px-2 py-1">Page {data.page} of {data.total_pages}</span>
            <button disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">
              Next
            </button>
          </div>
        </div>
      )}

      {selectedId && <FrameworkDetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}
